import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Browser } from '@syncfusion/ej2-base';
import { CheckBoxSelection, Inject, MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';

import CaretDownIcon from 'assets/icon-angle-down.svg';
import CaretUpIcon from 'assets/icon-angle-up.svg';

import { MultipleSelectSearchWrapper } from './styles';

const LESS_LEFT = 1;
const MultipleSelectSearch = ({
  className,
  dataSource,
  fields,
  placeholder,
  defaultValue,
  error,
  onBlur,
  onChange,
  titleNotFound,
  isDisabled,
}) => {
  const [focused, setFocused] = useState(false);
  const [top, setTop] = useState(0);
  const mulObj = useRef();
  const mainElementRef = useRef();
  const { selectedDefaultLanguage } = useSelector((sta) => sta.topBar);
  const { t } = useTranslation();
  const loadDefaultValues = () => {
    dataSource.forEach((currentValue) => {
      if (currentValue) {
        if (defaultValue.some((value) => value === currentValue[fields.value])) {
          const name = currentValue[fields.text];
          mulObj.current.addValue(currentValue[fields.value], name, null);
          mulObj.current.addChip(name, currentValue[fields.value], null);
        }
      }
    });
  };

  useEffect(() => {
    const chips = document.querySelector('.e-chips-collection');
    if (chips) {
      chips.style.display = 'block';
    }
  }, [selectedDefaultLanguage]);

  useEffect(() => {
    if (mulObj.current) {
      if (!defaultValue?.length) {
        mulObj.current.value = '';
      } else {
        defaultValue.forEach((value) => {
          if (
            mulObj.current.value?.length &&
            !mulObj.current.value.some((currentValue) => currentValue === value)
          ) {
            mulObj.current.value.push(value);
            const item = dataSource.find((dataItem) => dataItem[fields.value] === value);
            const name = item[fields.text];
            mulObj.current.addChip(name, item[fields.value], null);
          }
        });
      }
    }
  }, [defaultValue]);

  const onBlurInternal = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const change = () => {
    if (mulObj.current.viewWrapper) {
      mulObj.current.viewWrapper.style.display = 'none';
    }
    if (mulObj.current.chipCollectionWrapper) {
      mulObj.current.chipCollectionWrapper.style.display = 'block';
    }
    const inputPos = mulObj.current.overAllWrapper.getBoundingClientRect();
    const popupPos =
      mulObj.current.popupWrapper && mulObj.current.popupWrapper.getBoundingClientRect();
    if (inputPos && popupPos && inputPos.top + inputPos.height > popupPos.top) {
      mulObj.current.popupWrapper.style.top = `${inputPos.top + inputPos.height}px`;
    }
    if (inputPos) {
      mulObj.current.popupWrapper.style.left = `${inputPos.left - LESS_LEFT}px`;
    }
    setTop(0);
    if (mulObj.current.value.length >= 1) {
      setTop(0);
    }
    onChange(mulObj.current.value);
  };

  const onSelect = (args) => {
    const name = args.itemData[fields.text];
    mulObj.current.addChip(name, args.itemData[fields.value], args.e);
    setTimeout(() => {
      change(args);
    }, 50);
  };

  const onRemoved = (args) => {
    setTimeout(() => {
      change(args);
    }, 10);
  };

  const onSelectedAll = () => {
    mulObj.current.hidePopup();
  };

  const onCreate = () => {
    mulObj.current.chipCollectionWrapper = mulObj.current.createElement('span', {
      className: 'e-chips-collection',
      styles: 'display:none',
    });
    mulObj.current.iconDropDown = mulObj.current.createElement('div', {
      className: 'caret',
    });
    mulObj.current.componentWrapper.appendChild(mulObj.current.chipCollectionWrapper);
    mulObj.current.componentWrapper.appendChild(mulObj.current.iconDropDown);
    if (defaultValue.length) {
      loadDefaultValues();
      setTimeout(() => {
        const viewWrapper = document.getElementsByClassName('e-delim-view e-delim-values');
        const chipCollectionWrapper = document.getElementsByClassName('e-chips-collection');
        if (viewWrapper[0] && chipCollectionWrapper[0]) {
          viewWrapper[0].style.display = 'none';
          chipCollectionWrapper[0].style.display = 'block';
        }
      }, 500);
    }
  };

  const onFocus = () => {
    mulObj.current.viewWrapper.style.display = 'none';
  };

  const onOpen = (event) => {
    if (Browser.isDevice) {
      const { popup } = event;
      popup.element.classList.remove('e-ddl-device', 'e-ddl-device-filter');
      popup.element.style.width = `${parseInt(mainElementRef.current.offsetWidth - 2, 10)}px`;
      popup.element.style.maxHeight = `${parseInt(220, 10)}px`;
      popup.element.querySelector('.e-content.e-dropdownbase').style.maxHeight = `${
        parseInt(220, 10) - 25
      }px`;
      popup.element.querySelector('.e-content.e-dropdownbase').style.height = 'auto';
      popup.collision = { X: 'flip', Y: 'flip' };
      popup.position = { X: 'left', Y: 'bottom' };
      popup.element.style.top = `${parseInt(mainElementRef.current.offsetTop, 10)}px`;
      popup.element.style.left = `${parseInt(mainElementRef.current.offsetLeft, 10)}px`;
      popup.dataBind();
    }
    const inputPos = mulObj.current.overAllWrapper.getBoundingClientRect();
    if (inputPos) {
      setTimeout(() => {
        mulObj.current.popupWrapper.style.left = `${inputPos.left - LESS_LEFT}px`;
        setFocused(true);
      }, 140);
    }
  };

  const onClose = () => {
    setFocused(false);
  };

  return (
    <MultipleSelectSearchWrapper
      className={classNames(
        {
          error,
          focus: focused,
        },
        className
      )}
      top={top}
      caret={focused ? CaretUpIcon : CaretDownIcon}
      ref={mainElementRef}
      isDisabled={isDisabled}
    >
      <MultiSelectComponent
        className={classNames(className)}
        ref={mulObj}
        cssClass="popup-multiselect"
        dataSource={dataSource}
        fields={fields}
        placeholder={placeholder}
        filterType="Contains"
        filterBarPlaceholder={t('general_search')}
        mode="CheckBox"
        closePopupOnSelect={false}
        showClearButton
        showSelectAll
        selectAllText={t('general_all')}
        unSelectAllText={t('general_all')}
        open={onOpen}
        close={onClose}
        change={change}
        select={onSelect}
        created={onCreate}
        focus={onFocus}
        selectedAll={onSelectedAll}
        removed={onRemoved}
        onBlur={onBlurInternal}
        noRecordsTemplate={titleNotFound === '' ? t('general_search') : titleNotFound}
        ignoreAccent
        disabled={isDisabled}
      >
        <Inject services={[CheckBoxSelection]} />
      </MultiSelectComponent>
    </MultipleSelectSearchWrapper>
  );
};

MultipleSelectSearch.defaultProps = {
  className: '',
  placeholder: '',
  titleNotFound: '',
  defaultValue: [],
  error: false,
  onBlur: undefined,
  onChange: () => null,
  isDisabled: false,
};

MultipleSelectSearch.propTypes = {
  className: PropTypes.string,
  dataSource: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fields: PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string)]),
  error: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  titleNotFound: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default MultipleSelectSearch;
